import { isNil, isNull, isObject } from "lodash-es";

import { buildParamsArray, composeBuilder } from "@/browser";

import { IURLParams, IURLSearchParams, IURLSortConfig } from "./types";

const DEFAULT_SORT_PARAMS_PATTERN = ({ sortBy, order }: IURLSortConfig) => `${sortBy},${order}`;

const flatKeys = ([parent, obj]: [string, object]) => {
  return Object.entries(obj).reduce((prev, [key, value]) => {
    return { ...prev, [`${parent}.${key}`]: value };
  }, {});
};

export const pathCompiler = (path: string, params: IURLSearchParams & IURLParams) => {
  for (const key in params) {
    if (Object.prototype.hasOwnProperty.call(params, key) && !isNil(params[key])) {
      const val = params[key];
      path = path.replace(`{${key}}`, val.toString());
    }
  }

  return path;
};

const convertToQueryStringFormat = (paramArray: [string, any]) =>
  !isNull(paramArray[1]) ? `${paramArray[0]}=${encodeURIComponent(paramArray[1])}` : paramArray[0];

export const searchFrom = (
  { page, size, filtersConfig = {}, sortConfig = [], ...rest }: IURLSearchParams = {},
  sortPattern = DEFAULT_SORT_PARAMS_PATTERN,
) => {
  const params = buildParamsArray({ page, size, ...filtersConfig, ...rest }).map((paramArray: [string, any]) => {
    if (isObject(paramArray[1])) {
      return buildParamsArray(flatKeys(paramArray)).map(convertToQueryStringFormat).join("&");
    }
    return convertToQueryStringFormat(paramArray);
  });

  const sortArray = buildParamsArray({ sort: sortConfig.map((s: IURLSortConfig) => sortPattern(s)) }).map(
    (paramArray: [string, string]) => `${paramArray[0]}=${paramArray[1]}`,
  );

  return params.concat(sortArray).join("&");
};

export const compose = composeBuilder(pathCompiler, searchFrom);

export const addHttpsProtocol = (url: string) => {
  if (!/^(?:f|ht)tps?:\/\//.test(url)) {
    url = "http://" + url;
  }
  return url;
};

export const getBaseURL = () => {
  if (window) {
    return `${window.location.protocol}//${window.location.host}`;
  } else {
    return "";
  }
};

export const getQueryVariable = (varName: string): string | undefined => {
  return parseQuery()[varName];
};

export const parseQuery = (): Record<string, any> => {
  const search = window.location.search.substring(1);
  const pairs = search.split("&");
  const query: Record<string, any> = {};

  pairs.forEach(pair => {
    const [key, value] = pair.split("=");
    query[decodeURIComponent(key)] = decodeURIComponent(value);
  });

  return query;
};
